import React, { useEffect } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initLenis } from "@/lib/lenis";
import Header from "@/components/mazaro/Header";
import Hero from "@/components/mazaro/Hero";
import Marquee from "@/components/mazaro/Marquee";
import Collection from "@/components/mazaro/Collection";
import TechBento from "@/components/mazaro/TechBento";
import SpecExplorer from "@/components/mazaro/SpecExplorer";
import Story from "@/components/mazaro/Story";
import Athletes from "@/components/mazaro/Athletes";
import VipClub from "@/components/mazaro/VipClub";
import Footer from "@/components/mazaro/Footer";
import AuthModal from "@/components/mazaro/AuthModal";
import ComingSoon from "@/components/mazaro/ComingSoon";
import ChatWidget from "@/components/mazaro/ChatWidget";
import { PaymentSuccess, PaymentCancel } from "@/components/mazaro/PaymentResult";
import { COMING_SOON } from "@/lib/data";
import Impressum from "@/pages/legal/Impressum";
import Datenschutz from "@/pages/legal/Datenschutz";
import AGB from "@/pages/legal/AGB";
import Widerruf from "@/pages/legal/Widerruf";
import Widerrufsformular from "@/pages/legal/Widerrufsformular";
import Versand from "@/pages/legal/Versand";
import Rueckgabe from "@/pages/legal/Rueckgabe";
import Kontakt from "@/pages/legal/Kontakt";
import "@/App.css";

const LEGAL_ROUTES = {
  "/impressum": Impressum,
  "/datenschutz": Datenschutz,
  "/agb": AGB,
  "/widerruf": Widerruf,
  "/widerrufsformular": Widerrufsformular,
  "/versand": Versand,
  "/rueckgabe": Rueckgabe,
  "/kontakt": Kontakt,
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-ink text-white flex items-center justify-center p-10">
          Etwas ist schiefgelaufen. Bitte Seite neu laden.
        </div>
      );
    }
    return this.props.children;
  }
}

function AuthCallback() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    api
      .post("/auth/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        toast.success(`Willkommen, ${data.name || "Fighter"}`);
      })
      .catch(() => toast.error("Google-Anmeldung fehlgeschlagen."))
      .finally(() => navigate("/", { replace: true }));
  }, [hash, navigate, setUser]);

  return null;
}

function Landing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-ink text-white min-h-screen">
      <div className="grain" aria-hidden="true" />
      <Header />
      <main>
        <Hero />
        <Marquee />
        <Collection />
        <TechBento />
        <SpecExplorer />
        <Story />
        <Athletes />
        <VipClub />
      </main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname.startsWith("/payment/success")) return <PaymentSuccess />;
  if (location.pathname.startsWith("/payment/cancel")) return <PaymentCancel />;
  const LegalPage = LEGAL_ROUTES[location.pathname];
  if (LegalPage) return <LegalPage />;
  if (COMING_SOON) return <ComingSoon />;
  return <Landing />;
}

function App() {
  useEffect(() => initLenis(), []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ChatWidget />
          <AuthModal />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
